import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/contexts/AuthContext';

type WeatherCondition = 'sunny' | 'cloudy' | 'rainy' | 'snowy';

interface WeatherData {
  temperature_f: number;
  condition: WeatherCondition;
  description: string;
  weathercode: number;
  /**
   * When the reading was OBTAINED, as reported by the server.
   *
   * This used to be `new Date()` at parse time, which meant every reading
   * looked like it had just arrived — including one held over from a
   * previous successful fetch after the provider went down. A stale reading
   * that looks current is worse than no reading, because somebody may act
   * on a fact that stopped being true hours ago.
   */
  reportedAt: Date | null;
  /** CORE-BR: current observation vs forecast. Never inferred from wording. */
  observationKind: 'current_observation' | 'forecast' | 'unknown';
  /**
   * True when this reading is being shown after a failed refresh. The value
   * is real and is kept as context; it must not be presented as current.
   */
  isStale: boolean;
}

export function useCurrentWeather() {
  const { activeShop } = useAuth();
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [hasLocation, setHasLocation] = useState(true);

  /**
   * Mark a held-over reading as no longer confirmed.
   *
   * Every failure path funnels through here. Previously only the graceful
   * provider-fallback payload set this flag, so an invoke exception, a
   * network drop or a malformed response left the last successful reading on
   * screen looking freshly obtained for the whole outage — the one thing a
   * weather display must never do.
   */
  const markHeldReadingStale = useCallback(() => {
    setWeather(prev => (prev && !prev.isStale ? { ...prev, isStale: true } : prev));
  }, []);

  const fetchWeather = useCallback(async () => {
    if (!activeShop?.id) {
      setIsLoading(false);
      return;
    }
    
    setIsLoading(true);
    try {
      const { data, error: fnError } = await supabase.functions.invoke('get-weather', {
        body: { shop_id: activeShop.id }
      });

      const functionErrorMessage = getWeatherErrorMessage(fnError, data);
      if (isMissingLocationError(functionErrorMessage)) {
        setWeather(null);
        setHasLocation(false);
        setError(null);
        return;
      }
      
      if (fnError) throw fnError;

      // A response we cannot read is a failed refresh, not a reading.
      if (!data || typeof data !== 'object') {
        markHeldReadingStale();
        return;
      }
      
      // Check for location not configured
      if (data?.error?.includes('location') || data?.error?.includes('coordinates')) {
        setWeather(null);
        setHasLocation(false);
        setError(null);
        setIsLoading(false);
        return;
      }
      
      // Graceful upstream-fallback (Open-Meteo down): keep the pill mounted
      // and keep the last reading — but MARK it. Previously this branch
      // returned without touching state, so the previous reading stayed on
      // screen indistinguishable from a fresh one for as long as the outage
      // lasted.
      if (data?.fallback === true || data?.error === 'SERVICE_UNAVAILABLE') {
        console.warn('[useCurrentWeather] Weather service temporarily unavailable, will retry');
        markHeldReadingStale();
        setError(null);
        setHasLocation(true);
        return;
      }
      
      if (data?.weather) {
        // The server's own timestamp, not the browser's clock at parse time.
        const reportedRaw = data.reported_at ?? data.timestamp ?? null;
        const reportedAt = reportedRaw ? new Date(reportedRaw) : null;
        setWeather({
          temperature_f: data.weather.temperature_f,
          condition: mapToDisplayCondition(data.weather.weathercode ?? data.weather.condition),
          description: data.weather.description || '',
          weathercode: data.weather.weathercode ?? 0,
          reportedAt: reportedAt && !Number.isNaN(reportedAt.getTime()) ? reportedAt : null,
          observationKind: data.observation_kind ?? 'unknown',
          isStale: false
        });
        setError(null);
        setHasLocation(true);
      } else {
        // 2xx with no usable weather payload. Nothing new to show, so the
        // reading we are still displaying is no longer confirmed.
        markHeldReadingStale();
      }
    } catch (err: any) {
      const errorMessage = getWeatherErrorMessage(err);
      if (isMissingLocationError(errorMessage)) {
        setWeather(null);
        setHasLocation(false);
        setError(null);
        return;
      }

      console.error('[useCurrentWeather] Error:', err);
      // The path that was missing. An invoke exception or network error used
      // to set `error` and leave the held reading untouched — so the pill
      // went on presenting an old number as the current condition.
      markHeldReadingStale();
      setError(err.message || 'Failed to fetch weather');
    } finally {
      setIsLoading(false);
    }
  }, [activeShop?.id, markHeldReadingStale]);

  // Fetch on mount and every 10 minutes
  useEffect(() => {
    fetchWeather();
    const interval = setInterval(fetchWeather, 10 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchWeather]);

  return { weather, isLoading, error, hasLocation, refetch: fetchWeather };
}

function getWeatherErrorMessage(err: any, data?: any): string {
  if (typeof data?.error === 'string') {
    return data.error;
  }

  const contextBody = err?.context?.body;
  if (typeof contextBody === 'string') {
    try {
      const parsed = JSON.parse(contextBody);
      if (typeof parsed?.error === 'string') {
        return parsed.error;
      }
    } catch {
      return contextBody;
    }
  }

  return err?.message || '';
}

function isMissingLocationError(message: string): boolean {
  const normalized = message.toLowerCase();
  return normalized.includes('shop location not configured') ||
    normalized.includes('location not configured') ||
    normalized.includes('location coordinates required') ||
    normalized.includes('coordinates');
}

// Map weathercode to 4 display states
function mapToDisplayCondition(codeOrCondition: number | string): WeatherCondition {
  // If it's already a string condition, map it
  if (typeof codeOrCondition === 'string') {
    if (codeOrCondition === 'snow') return 'snowy';
    if (codeOrCondition === 'rain' || codeOrCondition === 'storm') return 'rainy';
    if (codeOrCondition === 'cloudy' || codeOrCondition === 'fog') return 'cloudy';
    return 'sunny';
  }
  
  const code = codeOrCondition;
  // Snow codes: 71-77 (snowfall), 85-86 (snow showers)
  if ((code >= 71 && code <= 77) || (code >= 85 && code <= 86)) return 'snowy';
  // Rain codes: 51-67 (drizzle/rain), 80-82 (rain showers), 95-99 (thunderstorm)
  if ((code >= 51 && code <= 67) || (code >= 80 && code <= 82) || (code >= 95 && code <= 99)) return 'rainy';
  // Cloudy/Fog codes: 3 (overcast), 45-48 (fog)
  if (code === 3 || code === 45 || code === 48) return 'cloudy';
  // Default: sunny (0=clear, 1=mainly clear, 2=partly cloudy)
  return 'sunny';
}
